import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, of } from "rxjs";
import { UnitGroups } from "../models/unit-groups";
import { config } from "../../shared/config";
import { catchError, map } from "rxjs/operators";
import { BaseHttpService } from "../../core/services/base-http.service";
import { StorageService } from "../../core/storage/storage.service";

@Injectable({
  providedIn: "root",
})
export class UnitGroupsService extends BaseHttpService {
  public selectedGroup: Observable<UnitGroups>;
  private selectedGroupSubject: BehaviorSubject<UnitGroups>;
  private defaultGroups: UnitGroups;

  constructor(
    protected _http: HttpClient,
    protected _storage: StorageService,
  ) {
    super(_http, _storage);
    this.selectedGroupSubject = new BehaviorSubject<UnitGroups>(null);
    this.selectedGroup = this.selectedGroupSubject.asObservable();
  }

  public get selectedGroupValue(): UnitGroups {
    return this.selectedGroupSubject.value;
  }

  public get defaultGroup(): UnitGroups {
    return this.defaultGroups;
  }

  setDefaultGroup(group: UnitGroups) {
    this.defaultGroups = group;
  }

  setSelectedGroup(group: UnitGroups) {
    this.selectedGroupSubject.next(group);
  }

  getUnitGroups(): Observable<UnitGroups[]> {
    const fields = ["*"];
    const url = config.generateApiUrl("UnitGroups", fields, null, "2.2");

    return this.get(url).pipe(
      map((groups) => {
        const gr = groups["data"] || { data: { items: [] } };
        return gr.items as UnitGroups[];
      })
    );
  }

  addUnitGroup(unitGroupName: string): Observable<boolean> {
    const body = { unitGroupName };
    return this._http
      .post(config.generateApiUrl(`UnitGroups`, null, null, "2.2"), body)
      .pipe(
        map(() => true),
        catchError(() => of(false))
      );
  }

  deleteUnitGroup(
    unitGroupId: number,
    unitGroupName: string
  ): Observable<boolean> {
    const encodedUnitGroupName = encodeURIComponent(unitGroupName);
    return this._http
      .delete(
        config.generateApiUrl(
          `UnitGroups/${unitGroupId}/unitGroupName/${encodedUnitGroupName}`,
          null,
          null,
          "2.2"
        )
      )
      .pipe(
        map(() => true),
        catchError(() => of(false))
      );
  }

  addUnitToGroup(
    unitGroupId: number,
    unitId: number,
    unitCode: number,
    unitGroupName: string
  ): Observable<boolean> {
    const body = { unitGroupId, unitId, unitCode, unitGroupName };
    return this._http
      .post(
        config.generateApiUrl(
          `UnitGroups/addUnitToUnitGroup`,
          null,
          null,
          "2.2"
        ),
        body
      )
      .pipe(
        map(() => true),
        catchError(() => of(false))
      );
  }

  deleteUnitFromGroup(
    unitGroupId: number,
    unitId: number,
    unitCode: number,
    groupName: string
  ): Observable<boolean> {
    const encodedGroupName = encodeURIComponent(groupName);
    return this._http
      .delete(
        config.generateApiUrl(
          `UnitGroups/${unitGroupId}/unitId/${unitId}/unitCode/${unitCode}/unitGroupName/${encodedGroupName}`,
          null,
          null,
          "2.2"
        )
      )
      .pipe(
        map(() => true),
        catchError(() => of(false))
      );
  }

  updateGroupName(
    unitGroupId: number,
    unitGroupName: string,
    oldUnitGroupName: string
  ): Observable<boolean> {
    const encodedUnitGroupName = encodeURIComponent(unitGroupName);
    const encodedOldUnitGroupName = encodeURIComponent(oldUnitGroupName);
    return this._http
      .put(
        config.generateApiUrl(
          `UnitGroups/${unitGroupId}/unitGroupName/${encodedUnitGroupName}/oldUnitGroupName/${encodedOldUnitGroupName}`,
          null,
          null,
          "2.2"
        ),
        {}
      )
      .pipe(
        map(() => true),
        catchError(() => of(false))
      );
  }

  updateGroupType(
      unitGroupId: number,
      type: number
  ): Observable<boolean> {
    return this._http
        .put(
            config.generateApiUrl(
                `UnitGroups/${unitGroupId}/type/${type}`,
                null,
                null,
                '2.2'
            ),
            {}
        )
        .pipe(
            map(() => true),
            catchError(() => of(false))
        );
  }
}
